import { type useCurrencyFormatter, NumberFormats as nf, shouldShowTimer } from '@shared/utils';
import type { useTranslations } from 'next-intl';
import type { GameDetails } from '../../casino/types/types';
import type { ScratchcardDetails } from '../../scratchcards/types';
import type { Step, StepProgress } from '../../StepProgressList/types';
import type { Amount, BonusInfoData, BundleStatus, Offer, OfferBundleDetails, OfferStepActions, StepStatus } from '../types';

const ALL_REWARDS_CLAIMED_BUNDLE_STATUSES = ['CLAIMED', 'REDEEMED', 'COMPLETED'] as const;

type AllRewardsClaimedBundleStatus = (typeof ALL_REWARDS_CLAIMED_BUNDLE_STATUSES)[number];

const isAllRewardsClaimedBundleStatus = (status?: string | null): status is AllRewardsClaimedBundleStatus => {
  if (!status) return false;
  return (ALL_REWARDS_CLAIMED_BUNDLE_STATUSES as readonly string[]).includes(status);
};

type Translator = ReturnType<typeof useTranslations>;

function translatorWithNamespace(translator: Translator, namespace: 'offers' | 'lotteries') {
  if (namespace === 'lotteries')
    return (key: string) => {
      const lotteryTranslationKey = `lotteries.${key}`;
      const hasTranslation = translator.has(lotteryTranslationKey);
      return hasTranslation ? translator(lotteryTranslationKey) : key;
    };
  return (key: string, params?: Record<string, string | number>) => translator(`offers.${key}`, params);
}

/**
 * This file is done just to handle the one small part of the logic,
 * this file will completely change once we implement the logic for all the offers,
 * it will be done in the next ticket
 */

/**
 * Maps step data to appropriate translation keys and parameters based on step type and action
 * @param step - Step data from backend
 * @param stepType - Type of step (promotion, offer, claim, play)
 * @param t - Translation function
 * @param currencyFormatter - Currency formatting function
 * @returns Object with translation key, parameters, and CTA information
 */
function mapStepToTranslation(
  step: {
    action: string;
    initialAmount?: Pick<Amount, 'fixed_amount' | 'currency'>;
    initialNumber?: number;
    initialPercentage?: number;
    label?: string;
    productName?: string;
  },
  stepType: 'promotion' | 'offer' | 'claim' | 'play',
  translator: Translator,
  currencyFormatter: ReturnType<typeof useCurrencyFormatter>,
  games?: GameDetails[],
  scratchcards?: ScratchcardDetails[]
): { stepKey: string; stepText: string; ctaKey: string; ctaText: string } {
  let stepKey = '';
  let ctaKey = '';
  const params: Record<string, string | number> = {};
  const t = translatorWithNamespace(translator, 'offers');
  const tLotteries = translatorWithNamespace(translator, 'lotteries');
  const gameFallback = t('page.obs.offer.step.unlock.stake.fallback.game');
  const scratchcardFallback = t('page.obs.offer.step.unlock.stake.fallback.scratchcard');
  const lotteryFallback = t('page.obs.offer.step.unlock.stake.fallback.lottery');
  const gameDisplayName = games?.find(game => game.name === step.productName)?.displayName || step.productName || gameFallback;
  const scratchcardDisplayName = scratchcards?.find(scratchcard => scratchcard.gameCode === step.productName)?.localisedName || step.productName || scratchcardFallback;
  const lotteryDisplayName = tLotteries(`${step.productName}.name`);

  if (stepType === 'promotion') {
    switch (step.action) {
      case 'OPT_IN':
        stepKey = 'page.obs.offer.step.unlock.optin';
        ctaKey = 'page.obs.offer.cta.optin';
        break;
      case 'DEPOSIT':
        stepKey = 'page.obs.offer.step.unlock.deposit';
        ctaKey = 'page.obs.offer.cta.deposit';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
        break;
      default:
        stepKey = 'page.obs.offer.step.unlock.deposit';
        ctaKey = 'page.obs.offer.cta.deposit';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
    }
  } else if (stepType === 'offer') {
    switch (step.action) {
      case 'STAKE_SINGLE_GAME': {
        stepKey = 'page.obs.offer.step.unlock.stake.singleproduct';
        ctaKey = 'page.obs.offer.cta.play';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
        const gameDisplayName = games?.find(game => game.name === step.productName)?.displayName;

        params['1'] = gameDisplayName || step.productName || gameFallback;
        break;
      }
      case 'STAKE_SINGLE_PRODUCT': {
        stepKey = 'page.obs.offer.step.unlock.stake.singleproduct';
        ctaKey = 'page.obs.offer.cta.play';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
        const productFallback = t('page.obs.offer.step.unlock.stake.fallback.product');
        params['1'] = step.label || step.productName || productFallback;
        break;
      }
      case 'STAKE_SINGLE_LOTTERY': {
        stepKey = 'page.obs.offer.step.unlock.stake.singleproduct';
        ctaKey = 'page.obs.offer.cta.bet';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
        params['1'] = lotteryDisplayName || step.label || step.productName || lotteryFallback;
        break;
      }
      case 'STAKE_SINGLE_SCRATCHCARD': {
        stepKey = 'page.obs.offer.step.unlock.stake.singleproduct';
        ctaKey = 'page.obs.offer.cta.play';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }

        params['1'] = scratchcardDisplayName || step.label || step.productName || scratchcardFallback;
        break;
      }
      case 'STAKE_ALL_LOTTERIES':
      case 'STAKE_LOTTERIES':
        stepKey = 'page.obs.offer.step.unlock.stake.lotteries';
        ctaKey = 'page.obs.offer.cta.bet';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
        break;
      case 'STAKE_ALL_SCRATCHCARDS':
      case 'STAKE_SCRATCHCARDS':
        stepKey = 'page.obs.offer.step.unlock.stake.scratchcards';
        ctaKey = 'page.obs.offer.cta.play';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
        break;
      case 'STAKE_ALL_GAMES':
      case 'STAKE_GAMES':
        stepKey = 'page.obs.offer.step.unlock.stake.games';
        ctaKey = 'page.obs.offer.cta.play';
        if (step.initialAmount) {
          const formattedAmount = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
          params['0'] = formattedAmount;
        }
        break;
      case 'STAKE_GAMES_TEMPLATE':
      case 'STAKE_SELECTED_GAMES':
        stepKey = 'page.obs.offer.step.unlock.stake.selectedgames';
        ctaKey = 'page.obs.offer.cta.games';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
        break;
      case 'STAKE_ALL_VERTICALS':
        stepKey = 'page.obs.offer.step.unlock.stake.games';
        ctaKey = 'page.obs.offer.cta.bet';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
        break;
      case 'STAKE_ANY_PRODUCT':
        stepKey = 'page.obs.offer.step.unlock.stake.anyproduct';
        ctaKey = 'page.obs.offer.cta.play';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
        break;
      case 'SOLVE_DEPOSIT_FIRST_CONFLICT':
      case 'SOLVE_DEPOSIT_CONFLICT':
        stepKey = 'page.obs.offer.step.unlock.solve.depositfirstconflict';
        ctaKey = 'page.obs.offer.cta.contact';
        break;
      default:
        stepKey = 'page.obs.offer.step.unlock.stake.games';
        ctaKey = 'page.obs.offer.cta.play';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
    }
  } else if (stepType === 'claim') {
    switch (step.action) {
      case 'CLAIM_FREE_SPINS':
        stepKey = 'page.obs.offer.step.claim.freespins';
        ctaKey = 'page.obs.offer.cta.claim';
        if (step.initialNumber) {
          params['0'] = step.initialNumber;
        }
        break;
      case 'CLAIM_SPORTSBOOK_FREE_BET':
      case 'CLAIM_SPORTS_BETS':
        stepKey = 'page.obs.offer.step.claim.sportsbets.default';
        ctaKey = 'page.obs.offer.cta.claim';
        break;
      case 'CLAIM_SPORTSBOOK_FREE_BET_CONDITIONAL':
        stepKey = 'page.obs.offer.step.claim.sportsbets.conditional';
        ctaKey = 'page.obs.offer.cta.activate';
        break;
      case 'CLAIM_SPORTSBOOK_BETSLIP_BOOST':
        stepKey = 'page.obs.offer.step.claim.sportsbook.betslip_boost';
        ctaKey = 'page.obs.offer.cta.claim';
        break;
      case 'CLAIM_SPORTSBOOK_FREE_BET_WITH_AMOUNT':
      case 'CLAIM_SPORTS_BETS_AMOUNT':
        stepKey = 'page.obs.offer.step.claim.sportsbets.amount';
        ctaKey = 'page.obs.offer.cta.claim';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
        break;
      case 'CLAIM_BONUS_FIXED_AMOUNT':
      case 'CLAIM_BONUS_FIXED':
        stepKey = 'page.obs.offer.step.claim.bonus.fixed';
        ctaKey = 'page.obs.offer.cta.claim';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
        break;
      case 'CLAIM_BONUS_PERCENTAGE_AMOUNT':
      case 'CLAIM_BONUS_PERCENTAGE':
        stepKey = 'page.obs.offer.step.claim.bonus.percentage.default';
        ctaKey = 'page.obs.offer.cta.claim';
        if (step.initialAmount) {
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
        break;
      case 'CLAIM_BONUS_PERCENTAGE_UPTO':
        stepKey = 'page.obs.offer.step.claim.bonus.percentage.upto';
        ctaKey = 'page.obs.offer.cta.claim';
        if (step.initialPercentage) {
          params['0'] = step.initialPercentage;
        }
        if (step.initialAmount) {
          params['1'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
        break;
      default:
        if (step.initialNumber) {
          // If it has a number, assume it's free spins
          stepKey = 'page.obs.offer.step.claim.freespins';
          ctaKey = 'page.obs.offer.cta.claim';
          params['0'] = step.initialNumber;
        } else if (step.initialAmount) {
          stepKey = 'page.obs.offer.step.claim.bonus.fixed';
          ctaKey = 'page.obs.offer.cta.claim';
          params['0'] = currencyFormatter(step.initialAmount.fixed_amount).format(nf.WITHOUT_DECIMALS).get();
        }
    }
  } else if (stepType === 'play') {
    switch (step.action) {
      case 'PLAY_ALL_VERTICALS':
        stepKey = 'page.obs.offer.step.reward.default';
        ctaKey = 'page.obs.offer.cta.bet';
        break;
      case 'PLAY_GENERAL_BONUS':
        stepKey = 'page.obs.offer.step.reward.default';
        ctaKey = 'page.obs.offer.cta.play';
        break;
      case 'PLAY_ALL_LOTTERIES':
      case 'PLAY_BONUS_LOTTERIES':
        stepKey = 'page.obs.offer.step.reward.lotteries';
        ctaKey = 'page.obs.offer.cta.bet';
        break;
      case 'PLAY_SINGLE_LOTTERY':
        stepKey = 'page.obs.offer.step.reward.singleproduct';
        ctaKey = 'page.obs.offer.cta.bet';
        params['0'] = lotteryDisplayName;
        break;
      case 'PLAY_SYNDICATES':
      case 'PLAY_BONUS_SYNDICATES':
        stepKey = 'page.obs.offer.step.reward.syndicates';
        ctaKey = 'page.obs.offer.cta.bet';
        break;
      case 'PLAY_ALL_GAMES':
      case 'PLAY_BONUS_GAMES':
        stepKey = 'page.obs.offer.step.reward.games.default';
        ctaKey = 'page.obs.offer.cta.play';
        break;
      case 'PLAY_GAMES_TEMPLATE':
      case 'PLAY_BONUS_SELECTED_GAMES':
        stepKey = 'page.obs.offer.step.reward.games.selectedgames';
        ctaKey = 'page.obs.offer.cta.play';
        break;
      case 'PLAY_ALL_SCRATCHCARDS':
      case 'PLAY_BONUS_SCRATCHCARDS':
        stepKey = 'page.obs.offer.step.reward.scratchcards';
        ctaKey = 'page.obs.offer.cta.play';
        break;
      case 'PLAY_SINGLE_SCRATCHCARD':
        stepKey = 'page.obs.offer.step.reward.singleproduct';
        ctaKey = 'page.obs.offer.cta.play';
        params['0'] = scratchcardDisplayName;
        break;
      case 'PLAY_SINGLE_GAME':
      case 'PLAY_REWARD_SINGLE_PRODUCT':
      case 'PLAY_FREE_SPINS':
        stepKey = 'page.obs.offer.step.reward.singleproduct';
        ctaKey = 'page.obs.offer.cta.play';
        params['0'] = gameDisplayName;
        break;
      case 'PLAY_SPORTSBOOK':
      case 'PLAY_SPORTSBOOK_FREE_BET':
      case 'PLAY_BONUS_SPORTSBOOK':
        stepKey = 'page.obs.offer.step.reward.sportsbook.default';
        ctaKey = 'page.obs.offer.cta.play';
        break;
      case 'PLAY_SPORTSBOOK_FREE_BET_CONDITIONAL':
        stepKey = 'page.obs.offer.step.reward.sportsbook.conditional';
        ctaKey = 'page.obs.offer.cta.view.sportsbook';
        break;
      case 'PLAY_SPORTSBOOK_BETSLIP_BOOST':
        stepKey = 'page.obs.offer.step.reward.sportsbook.betslip_boost';
        ctaKey = 'page.obs.offer.cta.view.sportsbook';
        break;
      default:
        stepKey = 'page.obs.offer.step.reward.default';
        ctaKey = 'page.obs.offer.cta.play';
    }
  }

  const stepText = t(stepKey, params);
  const ctaText = t(ctaKey);

  return { stepKey, stepText, ctaKey, ctaText };
}

/**
 * Determines the step type based on action and offer type
 * @param action - The action from backend
 * @param offerType - The type of offer
 * @returns The step type category
 */
function determineStepType(action: string, offerType?: string): 'promotion' | 'offer' | 'claim' | 'play' {
  if (['OPT_IN', 'DEPOSIT'].includes(action)) {
    return 'promotion';
  }

  if (action.startsWith('STAKE_') || action === 'SOLVE_DEPOSIT_FIRST_CONFLICT') {
    return 'offer';
  }

  if (action.startsWith('CLAIM_')) {
    return 'claim';
  }

  if (action.startsWith('PLAY_')) {
    return 'play';
  }

  if (offerType === 'FREE_SPINS' && action.includes('CLAIM')) {
    return 'claim';
  }

  if (offerType === 'FREE_SPINS' && action.includes('PLAY')) {
    return 'play';
  }

  return 'offer';
}

/**
 * Processes the promotion unlock step (MainStep) with proper logic for different action types
 * @param stepData - The main step data from backend
 * @param isAuthenticated - Whether user is authenticated
 * @param t - Translation function
 * @param currencyFormatter - Currency formatting function
 * @param bundleStatus - The bundle status to determine step behavior
 * @returns Processed step data with labels, CTA text, and state information
 */
function calculateFreeSpinsProgress(stepData: {
  initialFreeSpins?: number;
  freeSpins?: number;
  initialNumber?: number;
  currentNumber?: number;
}): { max: number; current: number; labelFormat: 'currentOfMax' } | null {
  const { initialNumber, initialFreeSpins, freeSpins, currentNumber } = stepData;
  const initial = initialNumber ?? initialFreeSpins ?? freeSpins;
  const currentRemaining = currentNumber ?? freeSpins;

  if (initial !== undefined && currentRemaining !== undefined) {
    return {
      max: initial,
      current: Math.max(0, initial - currentRemaining),
      labelFormat: 'currentOfMax',
    };
  }

  return null;
}

function processPromotionUnlockStep(
  stepData: {
    status: string;
    action: string;
    initialAmount?: Pick<Amount, 'fixed_amount' | 'currency'>;
    currentAmount?: Pick<Amount, 'fixed_amount' | 'currency'>;
    initialNumber?: number;
    currentNumber?: number;
    initialPercentage?: number;
    label?: string;
    productName?: string;
    initialFreeSpins?: number;
    freeSpins?: number;
  },
  isAuthenticated: boolean,
  translator: Translator,
  currencyFormatter: ReturnType<typeof useCurrencyFormatter>,
  bundleStatus: BundleStatus,
  games?: GameDetails[],
  scratchcards?: ScratchcardDetails[]
) {
  const isLocked = stepData.status === 'LOCKED';
  const isActive = stepData.status === 'ACTIVE';
  const isOfferDisabled = bundleStatus === 'CLAIMED' || bundleStatus === 'EXPIRED' || bundleStatus === 'CANCELLED';
  const isStepDisabled = bundleStatus === 'EXPIRED' || bundleStatus === 'CANCELLED';
  const t = translatorWithNamespace(translator, 'offers');

  const stepType = determineStepType(stepData.action);
  const { stepText, ctaText } = mapStepToTranslation(stepData, stepType, translator, currencyFormatter, games, scratchcards);

  let stepState: 'default' | 'active' | 'completed' = 'default';

  if (bundleStatus === 'CLAIMED') {
    stepState = 'completed';
  } else if (bundleStatus === 'EXPIRED' || bundleStatus === 'CANCELLED') {
    if (stepData.status === 'COMPLETED') {
      stepState = 'completed';
    } else if (stepData.status === 'ACTIVE') {
      stepState = 'active';
    } else {
      stepState = 'default';
    }
  } else {
    if (!isAuthenticated) {
      stepState = 'default';
    } else if (isLocked) {
      stepState = 'default';
    } else if (stepData.status === 'COMPLETED') {
      stepState = 'completed';
    } else if (isActive) {
      stepState = 'active';
    }
  }

  let showProgress = false;
  let progressLabel = '';
  let progress: StepProgress | undefined = undefined;

  const isStakeAction = stepData.action.startsWith('STAKE_');
  const canShowProgress = isAuthenticated && (stepData.status === 'ACTIVE' || stepData.status === 'COMPLETED');

  if (canShowProgress && isStakeAction && stepData.currentAmount && stepData.initialAmount) {
    const current = stepData.currentAmount.fixed_amount / 100;
    const total = stepData.initialAmount.fixed_amount / 100;

    showProgress = true;
    progressLabel = t('page.obs.offer.step.stake.progress');
    progress = {
      max: total,
      current: current,
      isCurrency: true,
      labelFormat: 'remainingCount',
    };
  } else if (canShowProgress) {
    const freeSpinsProgress = calculateFreeSpinsProgress(stepData);

    if (freeSpinsProgress) {
      showProgress = true;
      progressLabel = t('page.free_spins_use_progress');
      progress = freeSpinsProgress;
    }
  }

  const isButtonDisabled = isLocked || !isAuthenticated || isOfferDisabled;

  let stepObject: Step;
  if (showProgress && progressLabel && progress) {
    stepObject = {
      label: stepText,
      state: stepState,
      showProgress: true,
      progressLabel,
      progress,
      disabled: isStepDisabled,
    };
  } else {
    stepObject = {
      label: stepText,
      state: stepState,
      showProgress: false,
      disabled: isStepDisabled,
    };
  }

  return {
    step: stepObject,
    cta: {
      text: ctaText,
      action: stepData.action,
      disabled: isButtonDisabled,
    },
    isLocked,
    actionType: stepData.action,
  };
}

function getStepMessage(stepIndex: number): string {
  return `Step ${stepIndex + 1}`;
}

function getActionButtonText(action: string, translator: Translator, currencyFormatter: ReturnType<typeof useCurrencyFormatter>): string | null {
  const stepType = determineStepType(action);
  const dummyStep = { action };
  const { ctaText } = mapStepToTranslation(dummyStep, stepType, translator, currencyFormatter);

  return ctaText;
}

function getStepDisplayInfo(
  step: {
    status: string;
    action?: string;
    initialAmount?: Pick<Amount, 'fixed_amount' | 'currency'>;
    currentAmount?: Pick<Amount, 'fixed_amount' | 'currency'>;
    initialNumber?: number;
    currentNumber?: number;
    initialPercentage?: number;
    label?: string;
    productName?: string;
    bonusInfoData?: BonusInfoData;
    expirationDate?: string;
    initialFreeSpins?: number;
    freeSpins?: number;
  },
  stepType: 'main' | 'offer',
  stepIndex: number,
  offerType: string | undefined,
  translator: Translator,
  currencyFormatter: ReturnType<typeof useCurrencyFormatter>,
  isAuthenticated?: boolean,
  bundleStatus?: BundleStatus,
  games?: GameDetails[],
  scratchcards?: ScratchcardDetails[]
) {
  let label = '';
  let state: 'default' | 'active' | 'completed' = 'default';
  let showProgress = false;
  let progressLabel = '';
  let progress: StepProgress | undefined = undefined;
  const t = translatorWithNamespace(translator, 'offers');
  const isStepDisabled = bundleStatus === 'EXPIRED' || bundleStatus === 'CANCELLED';

  const canShowActiveFeatures = isAuthenticated && step.status === 'ACTIVE';

  if (isAuthenticated === false) {
    state = 'default';
  } else {
    switch (step.status) {
      case 'LOCKED':
      case 'CANCELLED':
      case 'EXPIRED':
        state = 'default';
        break;
      case 'ACTIVE':
        state = 'active';
        break;
      case 'COMPLETED':
        state = 'completed';
        break;
      default:
        state = 'default';
    }
  }

  // Wagering progress should always be visible for WAGER type bonuses
  if (isAuthenticated && step.bonusInfoData && step.bonusInfoData.type === 'WAGER') {
    const current = step.bonusInfoData.currentWageredAmount?.fixed_amount ?? 0;
    const total = step.bonusInfoData.requiredWageredAmount?.fixed_amount ?? 0;

    showProgress = true;
    progressLabel = t('page.obs.offer.reward.wagering');
    progress = {
      max: total,
      current: current,
      isCurrency: true,
      labelFormat: 'currentOfMax',
    };
  } else if (canShowActiveFeatures && step.currentAmount && step.initialAmount && step.bonusInfoData?.type !== 'BASIC') {
    const current = step.currentAmount.fixed_amount / 100;
    const total = step.initialAmount.fixed_amount / 100;

    showProgress = true;
    progressLabel = t('page.obs.offer.step.stake.progress');
    progress = {
      max: total,
      current: current,
      isCurrency: true,
      labelFormat: 'remainingCount',
    };
  } else if (canShowActiveFeatures && step.action === 'PLAY_FREE_SPINS') {
    const freeSpinsProgress = calculateFreeSpinsProgress(step);

    if (freeSpinsProgress) {
      showProgress = true;
      progressLabel = t('page.free_spins_use_progress');
      progress = freeSpinsProgress;
    }
  }

  if (step.action) {
    const mappedStepType = stepType === 'main' ? 'promotion' : determineStepType(step.action, offerType);
    const { stepText } = mapStepToTranslation(
      {
        action: step.action,
        initialAmount: step.initialAmount,
        initialNumber: step.initialNumber,
        initialPercentage: step.initialPercentage,
        label: step.label,
        productName: step.productName,
      },
      mappedStepType,
      translator,
      currencyFormatter,
      games,
      scratchcards
    );
    label = stepText;
  } else {
    label = getStepMessage(stepIndex);
  }

  const expirationDate = step.bonusInfoData?.expireDate || step.expirationDate;
  const showCountdown = shouldShowTimer(expirationDate);

  if (showProgress && progressLabel && progress) {
    return {
      label,
      state,
      showProgress: true,
      progressLabel,
      progress,
      disabled: isStepDisabled,
      bonusInfoData: isAuthenticated ? step.bonusInfoData : undefined,
      actionType: step.action,
      status: step.status,
      expirationDate,
      showCountdown,
      showProgressTooltip: offerType === 'BONUS_FUNDS' && step.bonusInfoData?.type === 'WAGER',
    };
  } else {
    return {
      label,
      state,
      showProgress: false,
      disabled: isStepDisabled,
      bonusInfoData: isAuthenticated ? step.bonusInfoData : undefined,
      actionType: step.action,
      status: step.status,
      expirationDate,
      showCountdown,
      showProgressTooltip: false,
    };
  }
}

/**
 * Checks if a fixed amount value is greater than 0
 * Handles undefined/null values by treating them as 0
 */
function hasPositiveAmount(amount: { fixed_amount?: number } | undefined): boolean {
  return (amount?.fixed_amount ?? 0) > 0;
}

/**
 * Determines if the winnings info box should be displayed for an offer
 * @param offer The offer to check
 * @param steps The steps associated with the offer (without promotionUnlockStep)
 * @param bundleStatus The bundle status
 * @returns true if winnings should be shown, false otherwise
 */
function shouldShowWinnings(offer: Offer, steps: Step[], bundleStatus?: BundleStatus): boolean {
  if (!bundleStatus || bundleStatus !== 'CLAIMED') {
    return false;
  }
  const showWinnings = hasPositiveAmount(offer.winnings);

  if (offer.type === 'BONUS_FUNDS') {
    const hasWagerBonusWithReleasedAmount = steps.some(step => step.bonusInfoData?.type === 'WAGER' && hasPositiveAmount(step.bonusInfoData.releasedAmount));
    if (hasWagerBonusWithReleasedAmount) return true;
  }

  return showWinnings;
}

function getCurrentActiveStep(steps: Step[]): number {
  const activeIndex = steps.findIndex(step => step.state === 'active');
  return activeIndex !== -1 ? activeIndex : 0;
}

/**
 * Determines if the offer-level expiration date should be displayed
 * @param bundleStatus The bundle status
 * @param offers The offers in the bundle
 * @returns true if expiration date should be shown, false otherwise
 */
function shouldShowOfferExpirationDate(bundleStatus?: BundleStatus, offers?: Offer[]): boolean {
  // If there are no offers, don't show expiration date
  if (!offers || offers.length === 0) {
    return false;
  }

  // Check if all rewards (all steps in all offers) are completed
  const allRewardsClaimed = offers.every(offer => {
    if (!offer.steps || offer.steps.length === 0) return true;
    return offer.steps.every(step => step.status === 'COMPLETED');
  });

  // If all rewards are claimed, don't show expiration date
  if (allRewardsClaimed) {
    return false;
  }

  // Show expiration date if there are unclaimed rewards
  return true;
}

function processOfferData(
  data: OfferBundleDetails,
  isAuthenticated: boolean,
  translator: Translator,
  currencyFormatter: ReturnType<typeof useCurrencyFormatter>,
  games?: GameDetails[],
  scratchcards?: ScratchcardDetails[]
) {
  const mainStep = data.step;
  const bundleStatus = data.bundleStatus;

  const isOfferDisabled = bundleStatus === 'EXPIRED' || bundleStatus === 'CANCELLED';

  const promotionUnlockStep = processPromotionUnlockStep(
    {
      status: mainStep.status,
      action: mainStep.action,
      initialAmount: mainStep.initialAmount,
      currentAmount: mainStep.currentAmount,
      initialNumber: mainStep.initialNumber,
      currentNumber: mainStep.currentNumber,
      initialPercentage: mainStep.initialPercentage,
      label: mainStep.label,
      productName: mainStep.productName,
      initialFreeSpins: mainStep.initialFreeSpins,
      freeSpins: mainStep.freeSpins,
    },
    isAuthenticated,
    translator,
    currencyFormatter,
    bundleStatus,
    games,
    scratchcards
  );

  let offerSteps: Step[] = [];

  if (data.offers && data.offers.length > 0) {
    const firstOffer = data.offers[0];
    if (firstOffer && firstOffer.steps) {
      const offerType = firstOffer.type;

      offerSteps = firstOffer.steps.map((step, index) => {
        const { label, state, showProgress, progressLabel, progress, disabled, bonusInfoData, expirationDate, showCountdown, actionType, status, showProgressTooltip } =
          getStepDisplayInfo(step, 'offer', index + 1, offerType, translator, currencyFormatter, isAuthenticated, bundleStatus, games, scratchcards);

        if (showProgress && progressLabel && progress) {
          return {
            label,
            state,
            showProgress: true,
            progressLabel,
            progress,
            disabled,
            bonusInfoData,
            expirationDate,
            showCountdown,
            actionType: actionType as OfferStepActions | undefined,
            status: status as StepStatus | undefined,
            showProgressTooltip,
            description: getDescription(firstOffer, step.action),
          };
        } else {
          return {
            label,
            state,
            showProgress: false,
            disabled,
            bonusInfoData,
            expirationDate,
            showCountdown,
            actionType: actionType as OfferStepActions | undefined,
            status: status as StepStatus | undefined,
            description: getDescription(firstOffer, step.action),
          };
        }
      });
    }
  }

  const allSteps: Step[] = [promotionUnlockStep.step, ...offerSteps];

  const isSingleOfferCompleted = data.offers && data.offers.length === 1 && data.offers[0]?.status === 'COMPLETED';

  let currentAction: string | null = null;
  let currentButtonText: string | null = null;
  let productIds: string | string[] | undefined = undefined;
  let productUrl: string | undefined = undefined;

  if (isOfferDisabled) {
    currentAction = null;
    currentButtonText = null;
  } else if (isAuthenticated) {
    const activeStepIndex = getCurrentActiveStep(allSteps);
    if (activeStepIndex === 0) {
      if (isSingleOfferCompleted) {
        currentAction = null;
        currentButtonText = null;
      } else if (mainStep.action !== 'OPT_IN' || mainStep.status === 'ACTIVE') {
        currentAction = promotionUnlockStep.cta.action;
        currentButtonText = promotionUnlockStep.cta.text;
        productUrl = mainStep.productURL;
      }
    } else if (data.offers && data.offers.length > 0) {
      const firstOffer = data.offers[0];
      const offerStepIndex = activeStepIndex - 1;
      if (firstOffer && firstOffer.steps && firstOffer.steps[offerStepIndex]) {
        const activeOfferStep = firstOffer.steps[offerStepIndex];
        if (activeOfferStep.action) {
          const stepType = determineStepType(activeOfferStep.action, firstOffer.type);
          const { ctaText } = mapStepToTranslation(
            {
              action: activeOfferStep.action,
              initialAmount: activeOfferStep.initialAmount,
              initialNumber: activeOfferStep.initialNumber,
              initialPercentage: activeOfferStep.initialPercentage,
              label: activeOfferStep.label,
              productName: activeOfferStep.productName,
            },
            stepType,
            translator,
            currencyFormatter,
            games,
            scratchcards
          );
          currentAction = activeOfferStep.action;
          currentButtonText = ctaText;
          if (activeOfferStep.gamesTemplateGamesIds?.length) {
            productIds = activeOfferStep.gamesTemplateGamesIds?.length > 0 ? activeOfferStep.gamesTemplateGamesIds : activeOfferStep.gamesTemplateGamesIds[0];
          } else if (activeOfferStep.productName) {
            productIds = activeOfferStep.productName;
          }
          productUrl = activeOfferStep.productURL;
        }
      }
    }

    if (!currentAction && !isSingleOfferCompleted && (mainStep.action !== 'OPT_IN' || mainStep.status === 'ACTIVE')) {
      currentAction = promotionUnlockStep.cta.action;
      currentButtonText = promotionUnlockStep.cta.text;
      productIds = undefined;
      productUrl = mainStep.productURL;
    }
  }

  return {
    steps: allSteps,
    promotionUnlockStep: promotionUnlockStep,
    offerSteps: offerSteps,
    currentAction,
    currentButtonText,
    productIds,
    productUrl,
  };
}

type OfferGroup = {
  offer: Offer;
  steps: Step[];
  activeStepIndex: number | null;
  currentAction: string | null;
  currentButtonText: string | null;
  currentButtonDisabled: boolean;
  currentProducts: string | string[] | undefined;
  currentProductUrl: string | undefined;
};

type MultipleOffersResult = {
  promotionUnlockStep: {
    step: Step;
    cta: { action: string; text: string };
  };
  offerGroups: OfferGroup[];
  mainStepAction: string | null;
  mainStepButtonText: string | null;
  hasActiveMainStep: boolean;
};

/**
 * Check if an offer's dependency is satisfied
 * @param offer The offer to check
 * @param offers All offers in the bundle
 * @returns true if dependency is satisfied or no dependency exists
 */
function isDependencySatisfied(offer: Offer, offers: Offer[]): boolean {
  if (!offer.dependsOn) {
    return true; // No dependency means it's always satisfied
  }

  const dependentOffer = offers.find(offerItem => offerItem.code === offer.dependsOn);
  if (!dependentOffer) {
    // Dependency not found - treat as satisfied to avoid breaking the UI
    return true;
  }

  // Check if the dependent offer is claimed (has all steps completed)
  return dependentOffer.status === 'CLAIMED' || (dependentOffer.steps && dependentOffer.steps.every(step => step.status === 'COMPLETED'));
}

function processMultipleOfferData(
  data: OfferBundleDetails,
  isAuthenticated: boolean,
  translator: Translator,
  currencyFormatter: ReturnType<typeof useCurrencyFormatter>,
  games?: GameDetails[],
  scratchcards?: ScratchcardDetails[]
): MultipleOffersResult {
  const bundleStatus = data.bundleStatus;
  const isOfferDisabled = bundleStatus === 'EXPIRED' || bundleStatus === 'CANCELLED';

  const mainStep = data.step;

  if (!mainStep) {
    throw new Error(`Multiple offers data is missing the main step (data.step). Bundle status: ${data.bundleStatus}`);
  }

  const promotionUnlockStep = processPromotionUnlockStep(mainStep, isAuthenticated, translator, currencyFormatter, bundleStatus, games, scratchcards);

  const hasActiveMainStep = promotionUnlockStep.step.state === 'active';

  const offerGroups: OfferGroup[] = [];

  if (data.offers) {
    data.offers.forEach(offer => {
      const offerSteps: Step[] = [];
      let activeStepIndex: number | null = null;
      let currentAction: string | null = null;
      let currentButtonText: string | null = null;
      let currentButtonDisabled = true;
      let currentProducts: string | string[] | undefined = undefined;
      let currentProductUrl: string | undefined = undefined;

      // Check if this offer's dependency is satisfied
      const isDependencySatisfiedForOffer = isDependencySatisfied(offer, data.offers || []);

      if (offer.steps) {
        const isFirstOfferInBundle = data.offers && data.offers[0] === offer;

        offer.steps.forEach((step, stepIndex) => {
          const isStepDisabled = isOfferDisabled || !isAuthenticated || step.status === 'LOCKED' || !isDependencySatisfiedForOffer;
          const isStepActive = step.status === 'ACTIVE' && !isOfferDisabled && isAuthenticated && isDependencySatisfiedForOffer;

          if (isStepActive && activeStepIndex === null) {
            activeStepIndex = stepIndex;
          }

          // Special case: For timer scenarios with availableFrom dates, show buttons for first step of FIRST OFFER only when locked
          // This allows timer display even when the step is locked due to timing
          const hasAvailableFromDate = offer.availableFrom !== undefined;
          const isTimerScenario = hasAvailableFromDate && stepIndex === 0 && step.status === 'LOCKED' && !isOfferDisabled && isAuthenticated && isFirstOfferInBundle;

          if (activeStepIndex === null && isTimerScenario) {
            activeStepIndex = stepIndex;
          }

          const stepDisplayInfo = getStepDisplayInfo(step, 'offer', stepIndex, offer.type, translator, currencyFormatter, isAuthenticated, bundleStatus, games, scratchcards);

          const stepType = determineStepType(step.action as string, offer.type);
          const { ctaText } = mapStepToTranslation(
            {
              action: step.action as string,
              initialAmount: step.initialAmount,
              initialNumber: step.initialNumber,
              initialPercentage: step.initialPercentage,
              label: step.label,
              productName: step.productName,
            },
            stepType,
            translator,
            currencyFormatter,
            games,
            scratchcards
          );

          const finalDisabled = isStepDisabled;

          let finalState = stepDisplayInfo.state;
          if (step.status === 'COMPLETED') {
            finalState = 'completed';
          } else if (isStepActive) {
            finalState = 'active';
          }

          const stepObject: Step = stepDisplayInfo.showProgress
            ? {
                label: stepDisplayInfo.label,
                state: finalState,
                showProgress: true,
                progressLabel: stepDisplayInfo.progressLabel!,
                progress: stepDisplayInfo.progress!,
                disabled: finalDisabled,
                bonusInfoData: stepDisplayInfo.bonusInfoData,
                actionType: stepDisplayInfo.actionType as OfferStepActions | undefined,
                status: stepDisplayInfo.status as StepStatus | undefined,
                expirationDate: stepDisplayInfo.expirationDate,
                showCountdown: stepDisplayInfo.showCountdown,
                ...(stepDisplayInfo.showProgressTooltip !== undefined && {
                  showProgressTooltip: stepDisplayInfo.showProgressTooltip,
                }),
                description: getDescription(offer, step.action),
              }
            : {
                label: stepDisplayInfo.label,
                state: finalState,
                showProgress: false,
                disabled: finalDisabled,
                bonusInfoData: stepDisplayInfo.bonusInfoData,
                actionType: stepDisplayInfo.actionType as OfferStepActions | undefined,
                status: stepDisplayInfo.status as StepStatus | undefined,
                expirationDate: stepDisplayInfo.expirationDate,
                showCountdown: stepDisplayInfo.showCountdown,
                description: getDescription(offer, step.action),
              };

          offerSteps.push(stepObject);

          if (isStepActive && activeStepIndex === stepIndex && isDependencySatisfiedForOffer) {
            currentAction = step.action as string;
            currentButtonText = ctaText;
            currentButtonDisabled = isStepDisabled;

            if (step.gamesTemplateGamesIds?.length) {
              currentProducts = step.gamesTemplateGamesIds?.length > 0 ? step.gamesTemplateGamesIds : step.gamesTemplateGamesIds[0];
            } else if (step.productName) {
              currentProducts = step.productName;
            }
            currentProductUrl = step.productURL;
          }

          // For timer scenarios: set button text for first locked step of FIRST OFFER only
          // But only if dependency is satisfied
          if (activeStepIndex === stepIndex && isTimerScenario && isDependencySatisfiedForOffer) {
            currentAction = step.action as string;
            currentButtonText = ctaText;
            currentButtonDisabled = true; // Always disabled for locked steps
            currentProducts = undefined;
            currentProductUrl = undefined;
          }

          // If dependency is not satisfied, do not show CTA
          if (!isDependencySatisfiedForOffer && activeStepIndex === stepIndex) {
            currentAction = null;
            currentButtonText = null;
            currentButtonDisabled = true;
            currentProducts = undefined;
            currentProductUrl = undefined;
          }
        });
      }

      offerGroups.push({
        offer,
        steps: offerSteps,
        activeStepIndex,
        currentAction,
        currentButtonText,
        currentButtonDisabled,
        currentProducts,
        currentProductUrl,
      });
    });
  }

  let mainStepAction: string | null = null;
  let mainStepButtonText: string | null = null;

  if (hasActiveMainStep && !isOfferDisabled) {
    mainStepAction = promotionUnlockStep.cta.action;
    mainStepButtonText = promotionUnlockStep.cta.text;
  }

  return {
    promotionUnlockStep,
    offerGroups,
    mainStepAction,
    mainStepButtonText,
    hasActiveMainStep,
  };
}

function getDescription(offer: Offer, stepActionType?: OfferStepActions) {
  if (stepActionType === 'PLAY_SPORTSBOOK_FREE_BET_CONDITIONAL') return offer.description;
  return undefined;
}

export {
  ALL_REWARDS_CLAIMED_BUNDLE_STATUSES,
  determineStepType,
  getActionButtonText,
  getCurrentActiveStep,
  getDescription,
  getStepDisplayInfo,
  getStepMessage,
  hasPositiveAmount,
  isAllRewardsClaimedBundleStatus,
  mapStepToTranslation,
  processMultipleOfferData,
  processOfferData,
  processPromotionUnlockStep,
  shouldShowOfferExpirationDate,
  shouldShowWinnings,
  type AllRewardsClaimedBundleStatus,
  type MultipleOffersResult,
  type OfferGroup,
};
